/**
 * Global Watch-Providers Batch Manager for title cards.
 *
 * Same pattern as imdbBatch.js — collects provider-lookup requests from all
 * mounted MovieCarousel rows, debounces 100ms, fires ONE POST to
 * /api/watch-providers/bulk-cards, then broadcasts results.
 *
 * Cache-only endpoint — zero TMDB calls, always fast.
 * Cards without cache data fall back to the existing single PlatformChip.
 */
import { api } from "./api";

const DEBOUNCE_MS = 100;
const MAX_BATCH   = 150;

// Module-level cache: key = "movie:tmdbId" | "tv:tmdbId"
// value = [{key, name}] array (empty = no providers in cache)
export const watchProvidersCache = new Map();

class WatchProvidersBatchManager {
  constructor() {
    this._queue = []; // [{ keys: string[], resolve, reject }]
    this._timer = null;
  }

  /**
   * Register a list of items for bulk provider lookup.
   * items: Array<{media_type, tmdb_id}>
   */
  register(items) {
    if (!Array.isArray(items) || items.length === 0) return Promise.resolve([]);

    const toFetch = items.filter(
      (it) => it?.tmdb_id && it?.media_type &&
              !watchProvidersCache.has(`${it.media_type}:${it.tmdb_id}`)
    );
    if (toFetch.length === 0) return Promise.resolve([]);

    const keys = toFetch.map((it) => `${it.media_type}:${it.tmdb_id}`);

    return new Promise((resolve, reject) => {
      this._queue.push({ keys, resolve, reject });
      this._schedule();
    });
  }

  _schedule() {
    if (this._timer) return;
    const total = this._queue.reduce((s, q) => s + q.keys.length, 0);
    if (total >= MAX_BATCH) {
      this._flush();
      return;
    }
    this._timer = setTimeout(() => this._flush(), DEBOUNCE_MS);
  }

  async _flush() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    const batch = this._queue;
    this._queue = [];
    if (batch.length === 0) return;

    // Deduplicate keys across all queued registrations
    const allKeys = [...new Set(batch.flatMap((q) => q.keys))];

    try {
      const r = await api.post("/watch-providers/bulk-cards", { keys: allKeys });
      const results = r.data?.results || {};

      // Only cache non-empty results. Titles missing from the backend cache
      // (cold start, new title) return [] — we must NOT cache those or the card
      // will never retry when the backend warms up later.
      for (const k of allKeys) {
        const providers = results[k];
        if (providers && providers.length > 0) {
          watchProvidersCache.set(k, providers);
        }
        // Empty / missing → leave out of cache so per-card fallback can retry
      }

      // Notify all mounted MovieCard instances to re-render
      window.dispatchEvent(new CustomEvent("fm-watch-providers-update"));

      for (const q of batch) {
        q.resolve(q.keys.map((k) => watchProvidersCache.get(k) || []));
      }
    } catch {
      // Resolve empty on error — cards fall back to single-chip gracefully
      for (const q of batch) {
        q.resolve(q.keys.map(() => []));
      }
    }
  }
}

const watchProvidersBatch = new WatchProvidersBatchManager();
export default watchProvidersBatch;
